/**
 * Normalize /authn/brands/search and /authn/sub-brands/:id/search rows for dropdowns.
 * API may return snake_case fields: { brand, name, sub_brand, location, rating, type, ... }.
 */

export function unwrapHotelSearchList(response) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.content)) return response.content
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.data)) return response.data
  return []
}

export function getBrandSearchLabel(item) {
  if (item == null) return ""
  if (typeof item === "string" || typeof item === "number") return String(item)
  if (typeof item !== "object") return ""

  if (item.brand != null && typeof item.brand === "object") {
    const fromNested = getBrandSearchLabel(item.brand)
    if (fromNested) return fromNested
  }

  if (typeof item.brand === "string" && item.brand.trim()) return item.brand.trim()
  if (typeof item.name === "string" && item.name.trim()) return item.name.trim()
  if (typeof item.sub_brand === "string" && item.sub_brand.trim()) return item.sub_brand.trim()
  if (typeof item.subBrand === "string" && item.subBrand.trim()) return item.subBrand.trim()
  return ""
}

export function getPropertySearchLabel(item) {
  if (item == null) return ""
  if (typeof item === "string" || typeof item === "number") return String(item)
  if (typeof item !== "object") return ""

  if (item.sub_brand != null && typeof item.sub_brand === "object") {
    const fromNested = getPropertySearchLabel(item.sub_brand)
    if (fromNested) return fromNested
  }
  if (item.subBrand != null && typeof item.subBrand === "object") {
    const fromNested = getPropertySearchLabel(item.subBrand)
    if (fromNested) return fromNested
  }

  if (typeof item.sub_brand === "string" && item.sub_brand.trim()) return item.sub_brand.trim()
  if (typeof item.subBrand === "string" && item.subBrand.trim()) return item.subBrand.trim()
  if (typeof item.name === "string" && item.name.trim()) return item.name.trim()
  return getBrandSearchLabel(item)
}

export function getHotelSearchLocation(item) {
  if (!item || typeof item !== "object") return ""
  const loc = item.location
  if (typeof loc === "string" && loc.trim()) return loc.trim()
  if (loc && typeof loc === "object") {
    const parts = [loc.city, loc.state, loc.country].filter(Boolean)
    if (parts.length) return parts.join(", ")
  }
  return ""
}

export function mapBrandSearchToDropdownOption(item) {
  const label = getBrandSearchLabel(item)
  const location = getHotelSearchLocation(item)
  return {
    label,
    value: label,
    id: item?.id ?? item?.brandId ?? item?.brand_id ?? null,
    subLabel: location || undefined,
    raw: item,
  }
}

export function mapPropertySearchToDropdownOption(item) {
  const label = getPropertySearchLabel(item)
  const location = getHotelSearchLocation(item)
  return {
    label,
    value: label,
    id: item?.id ?? item?.subBrandId ?? item?.sub_brand_id ?? null,
    subLabel: location || undefined,
    raw: item,
  }
}

/** True when row looks like a raw /authn/brands or /authn/sub-brands search result. */
export function isRawHotelSearchRow(item) {
  if (!item || typeof item !== "object") return false
  if (item.label != null || item.value != null) return false
  return (
    item.brand != null ||
    item.name != null ||
    item.sub_brand != null ||
    item.subBrand != null ||
    item.tripadvisor_url != null ||
    item.social_handles != null
  )
}

export function normalizeDropdownOption(item, { preferProperty = false } = {}) {
  if (item == null) return { label: "", value: "", id: null }
  if (typeof item === "string" || typeof item === "number") {
    const text = String(item)
    return { label: text, value: text, id: null }
  }
  if (typeof item !== "object") return { label: "", value: "", id: null }

  if (isRawHotelSearchRow(item)) {
    return preferProperty
      ? mapPropertySearchToDropdownOption(item)
      : mapBrandSearchToDropdownOption(item)
  }

  const label =
    typeof item.label === "string" || typeof item.label === "number"
      ? String(item.label)
      : typeof item.value === "string" || typeof item.value === "number"
        ? String(item.value)
        : preferProperty
          ? getPropertySearchLabel(item)
          : getBrandSearchLabel(item)

  return {
    ...item,
    label,
    value: label,
    id: item.id ?? item.brandId ?? item.brand_id ?? item.subBrandId ?? item.sub_brand_id ?? null,
  }
}

export function safeHotelSearchDisplayText(value, { preferProperty = false } = {}) {
  if (value == null || value === "") return ""
  if (typeof value === "string" || typeof value === "number") return String(value)
  if (typeof value === "object") {
    return preferProperty ? getPropertySearchLabel(value) : getBrandSearchLabel(value)
  }
  return ""
}

export function getCompetitorDisplayName(competitor) {
  if (competitor == null) return ""
  if (typeof competitor === "string" || typeof competitor === "number") return String(competitor)
  if (typeof competitor !== "object") return ""
  if (typeof competitor.name === "string" && competitor.name.trim()) return competitor.name.trim()
  return getPropertySearchLabel(competitor) || getBrandSearchLabel(competitor) || "Competitor"
}

export function getCompetitorSubLabel(competitor) {
  if (!competitor || typeof competitor !== "object") return ""
  const parts = []
  if (typeof competitor.brand === "string" && competitor.brand.trim()) parts.push(competitor.brand.trim())
  const location = getHotelSearchLocation(competitor)
  if (location) parts.push(location)
  if (competitor.rating != null && competitor.rating !== "") {
    parts.push(`${Number(competitor.rating).toFixed(1)}★`)
  }
  return parts.join(" · ")
}

export function coerceHotelSearchTextField(value, options) {
  return safeHotelSearchDisplayText(value, options)
}
